import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <Link to="/" className="flex items-center gap-3">
          <BenevolCheckLogo size="sm" />
        </Link>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl text-indigo-900">Politique de Confidentialité</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-gray-700">
            <p className="text-sm text-gray-500">Dernière mise à jour : Janvier 2025</p>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">1. Responsable du traitement</h2>
              <p>
                Le responsable du traitement des données personnelles est :<br />
                <strong>France Bénévolat Côte d'Or</strong><br />
                Maison des Associations<br />
                2 rue des Corroyeurs, 21000 Dijon<br />
                Email : <a href="mailto:cotedor@francebenevolat.org" className="text-indigo-600 hover:underline">cotedor@francebenevolat.org</a>
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">2. Données collectées</h2>
              <p>Dans le cadre de l'utilisation de Bénévol'Check, nous collectons les données suivantes :</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Données d'identification</strong> : nom, prénom, adresse email</li>
                <li><strong>Données professionnelles</strong> : nom de l'association, fonction, adresse</li>
                <li><strong>Données relatives aux missions</strong> : description des missions, dates, volume horaire, compétences acquises</li>
                <li><strong>Données de connexion</strong> : logs de connexion, adresse IP</li>
                <li><strong>Logo de l'association</strong> : image téléchargée par les associations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">3. Finalités du traitement</h2>
              <p>Vos données sont collectées et traitées pour les finalités suivantes :</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Création et gestion de votre compte utilisateur</li>
                <li>Génération et validation des attestations de compétences bénévoles</li>
                <li>Communication relative à votre compte et aux attestations</li>
                <li>Gestion des adhésions et renouvellements pour les associations</li>
                <li>Amélioration de nos services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">4. Base légale du traitement</h2>
              <p>Le traitement de vos données repose sur :</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Votre consentement</strong> : lors de la création de votre compte et l'acceptation des CGU</li>
                <li><strong>L'exécution d'un contrat</strong> : pour la fourniture du service de certification</li>
                <li><strong>L'intérêt légitime</strong> : pour l'amélioration de nos services et la sécurité</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">5. Durée de conservation</h2>
              <p>Vos données sont conservées pendant les durées suivantes :</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Données de compte</strong> : jusqu'à la suppression du compte + 1 an</li>
                <li><strong>Attestations</strong> : 10 ans (valeur probatoire)</li>
                <li><strong>Logs de connexion</strong> : 1 an</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">6. Destinataires des données</h2>
              <p>Vos données peuvent être transmises à :</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Les responsables d'associations (pour la validation des attestations)</li>
                <li>Nos sous-traitants techniques (hébergement, envoi d'emails)</li>
                <li>Les autorités compétentes en cas de réquisition légale</li>
              </ul>
              <p className="mt-2">
                Nous ne vendons ni ne partageons vos données à des fins commerciales.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">7. Vos droits</h2>
              <p>Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
                <li><strong>Droit de rectification</strong> : corriger vos données inexactes</li>
                <li><strong>Droit à l'effacement</strong> : supprimer vos données (via la page Profil)</li>
                <li><strong>Droit à la limitation</strong> : restreindre le traitement</li>
                <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition</strong> : vous opposer au traitement</li>
              </ul>
              <p className="mt-2">
                Pour exercer ces droits, contactez-nous à : <a href="mailto:cotedor@francebenevolat.org" className="text-indigo-600 hover:underline">cotedor@francebenevolat.org</a>
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">8. Cookies</h2>
              <p>
                Bénévol'Check utilise uniquement des cookies techniques essentiels au fonctionnement du service 
                (authentification, session). Nous n'utilisons pas de cookies de tracking ou publicitaires.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">9. Sécurité</h2>
              <p>
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger 
                vos données : chiffrement, contrôle d'accès, sauvegardes régulières.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">10. Réclamation</h2>
              <p>
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation 
                auprès de la CNIL : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">www.cnil.fr</a>
              </p>
            </section>

            <div className="pt-6">
              <Link to="/">
                <Button variant="outline">← Retour à l'accueil</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-indigo-900 text-white mt-16 py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm">&copy; 2025 Bénévol'Check</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
